/**
 * OB Config Consistency diagnostic card.
 *
 * Runs the env/URL consistency check against the current live config and
 * four synthetic combos. Displays a table with per-row pass/fail markers
 * so the operator can confirm mixed configs are rejected and the current
 * config is consistent.
 */

import { useState } from 'react';
import { Loader2, ShieldCheck, ShieldAlert, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function PassFailBadge({ pass: ok }) {
  if (ok) {
    return (
      <span className="inline-flex items-center gap-1 text-green-700">
        <CheckCircle2 className="h-3.5 w-3.5" />
        <span className="text-xs font-medium">consistent</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-red-700">
      <XCircle className="h-3.5 w-3.5" />
      <span className="text-xs font-medium">rejected</span>
    </span>
  );
}

export function OBConfigConsistencyPanel() {
  var [loading, setLoading] = useState(false);
  var [data, setData] = useState(null);

  var runCheck = async function () {
    setLoading(true);
    setData(null);
    try {
      var res = await diagnosticsAPI.obConfigConsistency();
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          {data && data.verdict === 'healthy' ? (
            <ShieldCheck className="h-5 w-5 text-green-600" />
          ) : data ? (
            <ShieldAlert className="h-5 w-5 text-red-600" />
          ) : (
            <ShieldCheck className="h-5 w-5 text-muted-foreground" />
          )}
          OB Config Consistency
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Verifies that the TrueLayer client_id prefix and URL hostnames agree on
          environment (sandbox vs production). Mixed configurations are rejected.
          Tests four synthetic combos alongside the current live config.
        </p>

        <Button onClick={runCheck} disabled={loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <ShieldCheck className="h-4 w-4 mr-2" />
          )}
          Run Check
        </Button>

        {data && (
          <div className="space-y-3">
            {/* Verdict */}
            <div className={
              'rounded-md px-3 py-2 text-sm font-medium ' +
              (data.verdict === 'healthy'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200')
            }>
              {data.verdict === 'healthy'
                ? 'Current config is consistent and mixed configs are correctly rejected.'
                : 'Guard issue detected. See rows below.'}
            </div>

            {/* Summary badges */}
            <div className="flex gap-2">
              <Badge variant={data.current_consistent ? 'outline' : 'destructive'}>
                Current: {data.current_consistent ? 'consistent' : 'inconsistent'}
              </Badge>
              <Badge variant={data.mixed_configs_rejected ? 'outline' : 'destructive'}>
                Mixed rejected: {data.mixed_configs_rejected ? 'yes' : 'NO'}
              </Badge>
            </div>

            {/* Results table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-1.5 pr-3 font-medium">Config</th>
                    <th className="py-1.5 pr-3 font-medium">Client ID</th>
                    <th className="py-1.5 pr-3 font-medium">Auth URL</th>
                    <th className="py-1.5 pr-3 font-medium">API URL</th>
                    <th className="py-1.5 pr-3 font-medium">Result</th>
                    <th className="py-1.5 font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map(function (row, i) {
                    return (
                      <tr key={i} className={
                        'border-b border-border/50' +
                        (row.label === 'current config' ? ' bg-muted/30 font-medium' : '')
                      }>
                        <td className="py-1.5 pr-3 text-xs">{row.label}</td>
                        <td className="py-1.5 pr-3">
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                            {row.client_id_class}
                          </Badge>
                        </td>
                        <td className="py-1.5 pr-3">
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                            {row.auth_url_class}
                          </Badge>
                        </td>
                        <td className="py-1.5 pr-3">
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                            {row.api_url_class}
                          </Badge>
                        </td>
                        <td className="py-1.5 pr-3">
                          <PassFailBadge pass={row.pass} />
                        </td>
                        <td className="py-1.5 text-xs text-muted-foreground max-w-[300px] truncate">
                          {row.reason}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
